import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { WorkspacePullState } from '@common/workspace/models/workspace-pull-state.model';
import { CollabPullDialogComponent, CollabPullDialogData } from '@portal-core/collab/components/collab-pull-dialog/collab-pull-dialog.component';
import { CurrentService } from '@portal-core/current/services/current.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { WorkspacePullProgressService } from '@portal-core/workspaces/services/workspace-pull-progress.service';
import { WorkspaceSessionService } from '@portal-core/workspaces/services/workspace-session.service';
import { distinctUntilChanged, map, Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';

/** How long the "a remote pull just started" pulse animation runs on the toolbar. Match the keyframes duration in SCSS. */
const RemoteStartPulseDurationMS = 1200;

@Component({
  selector: 'mc-workspace-pull-changes-bar',
  templateUrl: './workspace-pull-changes-bar.component.html',
  styleUrls: ['./workspace-pull-changes-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkspacePullChangesBarComponent implements OnInit, OnDestroy {
  @Input() readonly?: boolean = false;

  hasRemoteChanges: boolean = false;

  private destroy$: Subject<void> = new Subject<void>();

  @HostBinding('class.mc-workspace-pull-changes-bar-empty') get isEmpty(): boolean {
    return !this.hasRemoteChanges;
  }

  /**
   * Resolves the in-flight pull's initiator into a `LicenseUser` record so the bar can render their
   * avatar + name. Emits `null` when there's no running pull or the initiator user can't be resolved
   * (e.g. their license user record hasn't been loaded yet).
   */
  pullingUser$: Observable<LicenseUser | null>;

  /**
   * Set briefly when an observer-side pull transitions into the running state. Bound to a CSS class
   * on the toolbar that runs a one-shot pulse animation, so observers notice the bar changing into
   * "Pulling external changes by …" instead of missing the moment a remote pull began.
   */
  remoteStartPulse: boolean = false;

  /** Tracks the previous running-state so we only pulse on the running-edge transition. */
  private wasRunning: boolean = false;
  private pullStateSubscription: Subscription;
  private remoteStartPulseTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private currentService: CurrentService,
    private dialog: MatDialog,
    private licenseUsersService: LicenseUsersService,
    public workspaceSessionService: WorkspaceSessionService,
    public workspacePullProgressService: WorkspacePullProgressService
  ) {
    this.pullingUser$ = this.workspacePullProgressService.pullState$.pipe(
      // Only re-resolve when the running pull's initiator changes, not on every progress event
      // (which would otherwise cascade into a getLicenseUserByUserId$ call per percent tick).
      map(state => state?.status === 'running' ? state.startedByUserId : null),
      distinctUntilChanged(),
      switchMap(userId => {
        if (!userId) {
          return of<LicenseUser | null>(null);
        }
        const licenseId = this.workspaceSessionService.licenseId;
        if (typeof licenseId !== 'number') {
          return of<LicenseUser | null>(null);
        }
        return this.licenseUsersService.getLicenseUserByUserId$(userId, licenseId);
      })
    );

    // Watch for an *observer-side* transition into a running pull and trigger the pulse class
    // for a brief window. The initiator already knows what's happening (they clicked Pull), so
    // skip the pulse for them. This component is provided per workspace page, so the listener
    // lives for the page's lifetime — no perf concern.
    this.pullStateSubscription = this.workspacePullProgressService.pullState$.subscribe(state => {
      const isRunning = state?.status === 'running';
      const isInitiator = this.workspacePullProgressService.isInitiatorOfActivePull();

      if (isRunning && !this.wasRunning && !isInitiator) {
        this.triggerRemoteStartPulse();
      }

      this.wasRunning = isRunning;
    });
  }

  ngOnInit() {
    this.workspaceSessionService.hasRemoteChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(hasRemoteChanges => {
      this.hasRemoteChanges = hasRemoteChanges;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this.pullStateSubscription?.unsubscribe();
    if (this.remoteStartPulseTimeout !== null) {
      clearTimeout(this.remoteStartPulseTimeout);
      this.remoteStartPulseTimeout = null;
    }
  }

  private triggerRemoteStartPulse() {
    // If a previous pulse is still mid-animation, restart it cleanly.
    if (this.remoteStartPulseTimeout !== null) {
      clearTimeout(this.remoteStartPulseTimeout);
    }
    this.remoteStartPulse = true;
    this.cdr.markForCheck();
    this.remoteStartPulseTimeout = setTimeout(() => {
      this.remoteStartPulse = false;
      this.remoteStartPulseTimeout = null;
      this.cdr.markForCheck();
    }, RemoteStartPulseDurationMS);
  }

  isRunning(state: WorkspacePullState | null): boolean {
    return state?.status === 'running';
  }

  /** Compact progress label for the bar — overall percentage, no per-phase counts. */
  pullingLabel(state: WorkspacePullState | null): string {
    const percent = Math.round(WorkspacePullProgressService.aggregatePercent(state));
    return `${percent}%`;
  }

  onPullChangesClicked() {
    if (this.readonly) {
      return;
    }

    const projectId = this.workspaceSessionService.projectId;
    const branchName = this.workspaceSessionService.branchName;
    const licenseId = this.workspaceSessionService.licenseId;

    if (typeof projectId !== 'number' || !branchName || typeof licenseId !== 'number') {
      return;
    }

    this.dialog.open<CollabPullDialogComponent, CollabPullDialogData>(CollabPullDialogComponent, {
      ...CollabPullDialogComponent.DialogConfig,
      data: {
        branchName,
        licenseId,
        projectId,
        userId: this.currentService.getUserId()
      }
    });
  }
}
